import React, { useState, useEffect } from 'react';
import { Box, IconButton, Tooltip, Typography } from '@mui/material';
import { StitchIcon } from '../StitchIcons/icons';
import { VariantDropdown } from './VariantDropdown';
import { AlignmentSettings } from '../../types/pattern';
// validSectors removed — symmetry moved to MenuBar
import { STITCH_GROUPS, VARIANT_CONFIGS } from '../../stitches';
import { STORAGE_KEYS } from '../../utils/constants';
import { useLocalization } from '../../localization/LocalizationContext';

interface StitchesPanelProps {
  settings: AlignmentSettings;
  onChange: (settings: AlignmentSettings) => void;
  isNewProjectDialogOpen: boolean;
  onStitchSelect: (type: string) => void;
  selectedStitchType: string | null;
  selectedColor: string;
  onColorSelect: (color: string) => void;
}

const colors = [
  '#d14124', '#f18070', '#ff8f1c', '#ef6a00', '#de754c', '#c69214', '#eed484', '#78be21',
  '#9eb356', '#727337', '#1b806d', '#3e7054', '#05c3dd', '#5da3b2', '#008ad8', '#0057b7',
  '#435570', '#31137c', '#776cb1', '#8c4799', '#a57fb2', '#4a3041', '#382f2d', '#833921',
  '#c54644', '#fead77', '#ffd100', '#bba370', '#4e801f', '#00c389', '#77c19a', '#0085ad',
  '#2e5665', '#5c82a5', '#6f5091', '#403a60', '#5d295f', '#746e6e', '#9d9c9e', '#545859',
];

function getVariantName(variantId: string, lang: string): string {
  const cfg = VARIANT_CONFIGS[variantId];
  if (!cfg) return variantId;
  return cfg.names[lang] || cfg.names['en'] || variantId;
}

function getVariantAbbr(variantId: string, lang: string): string {
  const cfg = VARIANT_CONFIGS[variantId];
  if (!cfg) return '';
  return cfg.abbreviations[lang] || cfg.abbreviations['en'] || '';
}

export const StitchesPanel: React.FC<StitchesPanelProps> = ({
  settings, onChange, isNewProjectDialogOpen,
  onStitchSelect, selectedStitchType, selectedColor, onColorSelect,
}) => {
  const { t, language } = useLocalization();

  // Selected variant per group (persisted)
  const [selectedVariants, setSelectedVariants] = useState<Record<string, string>>(() => {
    try {
      const saved = localStorage.getItem(STORAGE_KEYS.SELECTED_VARIANTS);
      const parsed = saved ? JSON.parse(saved) : {};
      const defaults: Record<string, string> = {};
      for (const group of STITCH_GROUPS) {
        defaults[group.config.id] = parsed[group.config.id] || group.defaultVariantId;
      }
      return defaults;
    } catch {
      const defaults: Record<string, string> = {};
      for (const group of STITCH_GROUPS) {
        defaults[group.config.id] = group.defaultVariantId;
      }
      return defaults;
    }
  });

  useEffect(() => {
    localStorage.setItem(STORAGE_KEYS.SELECTED_VARIANTS, JSON.stringify(selectedVariants));
  }, [selectedVariants]);

  const handleVariantChange = (groupId: string, variantId: string) => {
    setSelectedVariants(prev => ({ ...prev, [groupId]: variantId }));
  };

  return (
    <Box sx={{
      width: 460, flexShrink: 0, p: 2, borderLeft: '1px solid #e0e0e0',
      display: 'flex', flexDirection: 'column', gap: 3,
      userSelect: 'none', overflowY: 'auto', minHeight: 0
    }}>
      {STITCH_GROUPS.length === 0 && (
        <Typography variant="caption" color="error">
          No stitches loaded. Restart the dev server.
        </Typography>
      )}
      {(['stitch', 'increase', 'decrease', 'misc', 'marker'] as const).map((category, catIdx) => {
        const groups = STITCH_GROUPS.filter(g => g.config.category === category);
        if (groups.length === 0) return null;
        return (
          <Box key={category}>
            {catIdx > 0 && <Box sx={{ borderTop: '1px solid #e0e0e0', mt: -1, mb: 1 }} />}
            <Box sx={{ display: 'grid', gridTemplateColumns: 'repeat(8, 1fr)', gap: 0.5 }}>
              {groups.map(group => {
                const activeVariant = selectedVariants[group.config.id] || group.defaultVariantId;
                const hasVariants = group.config.variants.length > 1;
                const isSelected = selectedStitchType === activeVariant;
                const abbr = getVariantAbbr(activeVariant, language);
                const name = getVariantName(activeVariant, language);
                return (
                  <Box key={group.config.id} sx={{ display: 'flex', flexDirection: 'column', alignItems: 'center' }}>
                    <Tooltip title={name} placement="top" enterDelay={500} disableInteractive>
                      <IconButton
                        onClick={() => onStitchSelect(activeVariant)}
                        sx={{
                          border: isSelected ? '2px solid #1976d2' : '1px solid #e0e0e0',
                          borderRadius: 1, p: 0.5, width: 42, height: 42,
                        }}
                      >
                        <StitchIcon variantId={activeVariant} size={28} color={selectedColor} />
                      </IconButton>
                    </Tooltip>
                    <Box sx={{ display: 'flex', alignItems: 'center', gap: 0, minHeight: 14, maxWidth: 48, justifyContent: 'center' }}>
                      <Typography variant="caption" sx={{ fontSize: 10, color: '#666', lineHeight: 1, whiteSpace: 'nowrap', overflow: 'hidden', textOverflow: 'ellipsis' }}>
                        {abbr}
                      </Typography>
                      {hasVariants && (
                        <VariantDropdown
                          group={group}
                          language={language}
                          color={selectedColor}
                          onSelect={(variantId) => {
                            handleVariantChange(group.config.id, variantId);
                            onStitchSelect(variantId);
                          }}
                        />
                      )}
                    </Box>
                  </Box>
                );
              })}
            </Box>
          </Box>
        );
      })}

      <Box>
        <Box sx={{ display: 'grid', gridTemplateColumns: 'repeat(8, 1fr)', gap: 1 }}>
          {colors.map((color) => (
            <Tooltip key={color} title={color} placement="top" enterDelay={500} disableInteractive>
              <IconButton
                onClick={() => onColorSelect(color)}
                sx={{
                  width: 32, height: 32, backgroundColor: color,
                  border: selectedColor === color ? '2px solid #1976d2' : '1px solid #e0e0e0',
                  borderRadius: 1,
                  '&:hover': { backgroundColor: color, opacity: 0.8 }
                }}
              />
            </Tooltip>
          ))}
        </Box>
      </Box>
    </Box>
  );
};
